import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Textarea } from "../components/ui/Textarea";
import { Switch } from "../components/ui/Switch";
import { ArrowLeft, Save, FileText } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { pageService } from "../services/pages";
import { toast } from "sonner";
import { RichTextEditor } from "../components/RichTextEditor";

interface PageData {
  id?: number;
  title: string;
  slug: string;
  subtitle?: string;
  content?: string;
  active?: boolean;
  version?: string;
  meta_title?: string;
  meta_description?: string;
}

interface NovaPaginaFormProps {
  onBack: () => void;
  pageData?: PageData;
}

export function NovaPaginaForm({ onBack }: NovaPaginaFormProps) {
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    id: undefined as number | undefined,
    titulo: "",
    slug: "",
    subtitle: "",
    conteudo: "",
    active: true,
    version: "",
    meta_title: "",
    meta_description: "",
  });

  useEffect(() => {
    if (id) {
      const fetchPage = async () => {
        try {
          setIsLoading(true);
          const response = await pageService.getPageByIdOrSlug(id);
          const page = response.data.data || response.data;
          setFormData({
            id: page.id,
            titulo: page.title,
            slug: page.slug,
            subtitle: page.subtitle || "",
            conteudo: page.content || "",
            active: page.active,
            version: page.version || "",
            meta_title: page.meta_title || "",
            meta_description: page.meta_description || "",
          });
        } catch (err) {
          console.error("Erro ao carregar a página", err);
          toast.error("Erro ao carregar os dados da página.");
        } finally {
          setIsLoading(false);
        }
      };
      fetchPage();
    }
  }, [id]);

  useEffect(() => {
    if (!isEditing && !formData.conteudo) {
      const wrapper = `<div class="max-w-4xl mx-auto px-4 py-16">
  <div class="bg-white">
    <div class="px-4">
      <div class="space-y-12 gap-4 flex flex-col">
        <p>Inicie o conteúdo da sua página aqui...</p>
      </div>
    </div>
  </div>
</div>`;
      setFormData(prev => ({ ...prev, conteudo: wrapper }));
    }
  }, [isEditing]);

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        title: formData.titulo,
        slug: formData.slug,
        subtitle: formData.subtitle,
        content: formData.conteudo,
        active: formData.active,
        version: formData.version || null,
        meta_title: formData.meta_title,
        meta_description: formData.meta_description,
        author_id: 1,
      };

      if (isEditing && formData.id) {
        await pageService.updatePage(formData.id, payload);
        toast.success("Página atualizada com sucesso!");
      } else {
        await pageService.createPage(payload);
        toast.success("Página criada com sucesso!");
      }
      onBack();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Erro ao salvar página. Verifique os dados.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-4 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Páginas
        </Button>
        <h1 className="text-2xl font-bold text-[#68686A]">
          {isEditing ? "Editar Página" : "Nova Página"}
        </h1>
        <p className="text-[#68686A]">
          {isEditing ? "Editar página estática existente" : "Criar nova página estática"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-7xl space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#68686A]">
                  <FileText className="h-5 w-5" />
                  Conteúdo da Página
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="titulo" className="text-sm font-medium text-[#68686A]">Título da Página</Label>
                  <Input
                    id="titulo"
                    placeholder="Ex: Sobre Nós, Política de Privacidade..."
                    required
                    value={formData.titulo}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                  />
                </div>

                <div>
                  <Label htmlFor="slug" className="text-sm font-medium text-[#68686A]">URL da Página (Slug)</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#68686A]">foradocloset.com/</span>
                    <Input
                      id="slug"
                      placeholder="sobre-nos"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      className="font-mono text-sm flex-1 border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subtitle" className="text-sm font-medium text-[#68686A]">Subtítulo</Label>
                  <Textarea
                    id="subtitle"
                    placeholder="Breve descrição do conteúdo da página..."
                    rows={2}
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#68686A] mb-2 block">Conteúdo da Página</Label>
                  <RichTextEditor 
                    value={formData.conteudo}
                    onChange={(content) => setFormData({ ...formData, conteudo: content })}
                    folder="paginas"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
              <CardHeader>
                <CardTitle className="text-[#68686A]">Configurações de Publicação</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="active" className="text-sm font-medium text-[#68686A]">Página Ativa</Label>
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked: boolean) => setFormData({ ...formData, active: checked })}
                  />
                </div>

                {(formData.slug === 'termos-de-uso' || formData.slug === 'privacidade') && (
                  <div>
                    <Label htmlFor="version" className="text-sm font-medium text-[#68686A]">Versão do Documento</Label>
                    <Input
                      id="version"
                      placeholder="Ex: v1.0, v2.3.1..."
                      value={formData.version}
                      onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                      className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="meta_title" className="text-sm font-medium text-[#68686A]">Meta Título (SEO)</Label>
                  <Input
                    id="meta_title"
                    placeholder="Título para motores de busca..."
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                  />
                </div>

                <div>
                  <Label htmlFor="meta_description" className="text-sm font-medium text-[#68686A]">Meta Descrição (SEO)</Label>
                  <Textarea
                    id="meta_description"
                    placeholder="Descrição para motores de busca..."
                    rows={3}
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
              <CardHeader>
                <CardTitle className="text-[#68686A]">Ações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#D4C070] hover:bg-[#849D70] text-white"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isLoading ? "Salvando..." : (isEditing ? "Atualizar Página" : "Publicar Página")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex gap-4 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
          >
            Cancelar
          </Button>
        </div>
      </form>

      {/* Fix para menus do TinyMCE que ficam abertos ou inacessíveis */}
      <style>{`
        .tox-tinymce-aux {
          z-index: 10000 !important;
        }
        .tox-opened {
          z-index: 10001 !important;
        }
      `}</style>
    </main>
  );
}

export default NovaPaginaForm;
